import React, { useState } from "react";
import { usePlaidLink } from "react-plaid-link";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Link2, Zap, Loader2, Sparkles } from "lucide-react";
import { useApp } from "../context/AppContext";
import { t } from "../lib/i18n";

const PlaidConnect = ({ onConnected }) => {
    const { user, language } = useApp();
    const [linkToken, setLinkToken] = useState(null);
    const [loading, setLoading] = useState(false);
    const [demoLoading, setDemoLoading] = useState(false);

    // Demo mode is a free-tier onboarding shortcut — hidden once the user has
    // paid (pro / elite) since they'll be connecting real accounts.
    const isFree = !user?.tier || user.tier === "explorer";

    const startLink = async () => {
        setLoading(true);
        try {
            const res = await api.post("/plaid/create_link_token");
            setLinkToken(res.data.link_token);
        } catch (e) {
            toast.error("No se pudo iniciar Caesar Link");
            setLoading(false);
        }
    };

    const { open, ready } = usePlaidLink({
        token: linkToken,
        onSuccess: async (public_token) => {
            try {
                await api.post("/plaid/exchange_public_token", { public_token });
                toast.success(language === "es" ? "Cuenta conectada" : "Account connected");
                onConnected && onConnected();
            } catch (e) {
                toast.error("Error al intercambiar token");
            } finally {
                setLoading(false);
                setLinkToken(null);
            }
        },
        onExit: () => {
            setLoading(false);
            setLinkToken(null);
        },
    });

    React.useEffect(() => {
        if (linkToken && ready) open();
    }, [linkToken, ready, open]);

    const startDemo = async () => {
        setDemoLoading(true);
        try {
            const res = await api.post("/plaid/sandbox_connect");
            toast.success(`${res.data.accounts} ${language === "es" ? "cuentas" : "accounts"} · ${res.data.transactions} tx`);
            onConnected && onConnected();
        } catch (e) {
            toast.error(language === "es" ? "Error activando demo" : "Failed to start demo");
        } finally {
            setDemoLoading(false);
        }
    };

    return (
        <div className="space-y-4">
            {/* DEMO — primary CTA, free tier only.
                Uses a self-contained dark surface so it stays legible regardless
                of whether the parent card is dark or a bright primary hero. */}
            {isFree && (
                <div className="rounded-2xl border border-white/10 p-4 space-y-3 shadow-[0_10px_30px_rgba(0,0,0,0.35)]"
                     style={{ background: "rgba(11, 14, 20, 0.92)", backdropFilter: "blur(8px)" }}
                     data-testid="demo-mode-card">
                    <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0"
                             style={{ background: "linear-gradient(135deg, #0AFFB0 0%, #B36CFF 100%)" }}>
                            <Sparkles className="w-5 h-5 text-[hsl(var(--ink))]" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                                <span className="font-display font-bold text-white text-base">
                                    {language === "es" ? "Prueba el demo" : "Try demo mode"}
                                </span>
                                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.16em]"
                                      style={{ background: "#0AFFB020", color: "#0AFFB0", border: "1px solid #0AFFB066" }}>
                                    {language === "es" ? "GRATIS" : "FREE"}
                                </span>
                            </div>
                            <p className="text-sm text-white/75 leading-relaxed">
                                {language === "es"
                                    ? "Activamos datos de ejemplo (cuentas, transacciones y suscripciones ficticias) para que veas todo el software funcionando sin conectar tu banco real."
                                    : "We'll load example data (fake accounts, transactions and subscriptions) so you can see the whole product in action without connecting your real bank."}
                            </p>
                        </div>
                    </div>
                    <Button
                        data-testid="plaid-quick-connect-button"
                        onClick={startDemo}
                        disabled={demoLoading}
                        className="w-full pill bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))] gap-2"
                    >
                        {demoLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        {language === "es" ? "Activar demo" : "Start demo"}
                    </Button>
                </div>
            )}

            {/* REAL bank link — always available */}
            <div className="flex flex-wrap gap-3">
                <Button
                    data-testid="plaid-connect-button"
                    onClick={startLink}
                    disabled={loading}
                    variant={isFree ? "outline" : "default"}
                    className={`pill gap-2 ${isFree ? "border-[hsl(var(--border))]" : "bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-[hsl(var(--primary-foreground))]"}`}
                >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                    {language === "es" ? "Conectá tu banco real" : "Connect your real bank"}
                </Button>
            </div>
        </div>
    );
};

export default PlaidConnect;
